import type { FeedLink } from '../publishing/feeds';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { listWarehouses } from '../organization/warehouses';
import type { Warehouse } from '../organization/warehouses';
import { allKinds } from '../catalog/partNames';
import type { PartKind } from '../catalog/partNames';
import { loadCached, refresh } from '../catalog/vehicles';
import type { Brand } from '../catalog/vehicles';
import { ColumnMenu } from './ColumnMenu';
import { useMounted } from '../ui/useMounted';
import {
  COLUMNS,
  FILTER_EMPTY,
  FILTER_PRESENT,
  columnValues,
} from '../inventory/catalog';
import { WHEEL_COLUMNS, wheelValues } from '../inventory/wheels';
import {
  feedUrl,
  filterableColumns,
  CONDITIONS,
  DEFAULT_INSTALLATION_TEMPLATE,
  PRICE_PLACEHOLDER,
  PACKET_ID_HINT,
  ROUNDING_STEPS,
  countMatching,
  createFeed,
  decimalOrNull,
  deleteFeed,
  deletedMark,
  deliveryLine,
  downloadMark,
  filterSummary,
  listDeletedFeeds,
  normalizePacketId,
  packetIdObstacle,
  listFeeds,
  renameFeed,
  rotateFeedUrl,
  setCredentials,
  setPacketId as savePacketId,
  setFeedFileName,
  setFeedStatus,
  setFilter,
  setSettings,
  statusLabel,
  wholeOrNull,
  type Feed,
  type FeedFilter,
} from '../publishing/feeds';

/**
 * Выгрузки на площадки и отбор товара в каждую.
 *
 * <p>Выгрузок на одну площадку бывает несколько: у живого клиента пять прайсов
 * на Дром, разложенных по ценовым диапазонам, — у каждого свой прайс-лист
 * в кабинете площадки и своя цена размещения. Различаются они только отбором.
 *
 * <p><b>Пустое поле — «без ограничения», а не «ничего».</b> Так и написано
 * рядом с полями: выгрузка, у которой стёрли цену, отдаёт весь склад. Обратное
 * прочтение стоит дорого — пустой прайс площадка примет молча, и объявления
 * пропадут вместе с накопленными просмотрами.
 *
 * <p>Ссылка меняется отдельной кнопкой с предупреждением: смена останавливает
 * выгрузку до тех пор, пока техспециалист площадки не пропишет новую в кабинете
 * клиента, а это не минуты.
 */
/**
 * @param role кабинет заводит только владелец: ссылка на прайс открывает
 *             склад целиком, и раздавать это право управляющему рано
 */
export function FeedsScreen({ role }: { role: string }) {
  const [feeds, setFeeds] = useState<Feed[] | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // Справочники берутся целиком: видов сто семьдесят восемь, марок триста
  // восемь. Это не четыре тысячи моделей — те в отборе не участвуют вовсе,
  // и городить ради этого поиск с сервера незачем.
  const [kinds, setKinds] = useState<PartKind[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [error, setError] = useState('');

  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  const load = useCallback(() => {
    listFeeds()
      .then((found) => {
        if (mounted.current) {
          setFeeds(found);
          setError('');
        }
      })
      .catch((cause) => {
        if (mounted.current) {
          setError(describe(cause, 'Выгрузки не загрузились'));
        }
      });
  }, [mounted]);

  useEffect(load, [load]);

  useEffect(() => {
    void listWarehouses()
      .then((found) => { if (mounted.current) setWarehouses(found); })
      // Молча: без списка складов отбор по цене всё ещё работает.
      .catch(() => { if (mounted.current) setWarehouses([]); });

    void allKinds()
      .then((found) => { if (mounted.current) setKinds(found); })
      .catch(() => { if (mounted.current) setKinds([]); });
    // Марки уже лежат в кэше справочника машин — он предзагружен ради
    // приёмки. Второй запрос за тем же был бы данью привычке.
    void loadCached()
      .then((cached) => (cached ? cached.brands : refresh().then((v) => v.brands)))
      .then((found) => { if (mounted.current) setBrands(found); })
      .catch(() => { if (mounted.current) setBrands([]); });
  }, [mounted]);

  if (feeds === null) {
    // Не удалось — так и говорим. Ошибка при загрузке оставляет состояние
    // пустым, и до разметки с сообщением дело не доходило: экран показывал
    // «Загружаем выгрузки» бесконечно, а причина — «сессия кончилась» —
    // лежала рядом непоказанной.
    return error === ''
      ? <p className="note">Загружаем выгрузки…</p>
      : <p className="note note--error">{error}</p>;
  }

  return (
    <section className="screen">
      <h2>Выгрузки</h2>
      <p className="note">
        Пустое поле отбора значит «без ограничения», а не «ничего»: такая
        выгрузка отдаёт весь склад.
      </p>

      {error && <p className="note note--error">{error}</p>}

      {feeds.length === 0 && (
        <p className="note">
          {role === 'OWNER'
            ? 'Выгрузок пока нет. Заведите кабинет — и появится постоянная ссылка на прайс.'
            : 'Выгрузок пока нет. Кабинет площадки заводит владелец.'}
        </p>
      )}

      {role === 'OWNER' && <NewFeed onCreated={load} />}

      <ul className="cards">
        {feeds.map((feed) => (
          <FeedCard
            key={feed.id}
            feed={feed}
            role={role}
            warehouses={warehouses}
            kinds={kinds}
            brands={brands}
            onChanged={load}
            onError={setError}
          />
        ))}
      </ul>

      <DeletedFeeds />
    </section>
  );
}

/**
 * Удалённые выгрузки — ради истории, и только.
 *
 * <p>Удаление помечает строку, а не убирает её: удалить запись значило бы
 * унести отметки о заборе прайса и об отправках, а спрашивают именно их —
 * «эта выгрузка вообще работала и когда её последний раз забирали». Строка,
 * удалённая полгода назад, отвечает на такой вопрос лучше, чем её отсутствие;
 * оставить ответ только в базе значило бы, что за ним снова идут
 * к разработчику — ровно та зависимость, ради устранения которой заведена
 * и сама отметка о заборе.
 *
 * <p>Свёрнутым разделом и запросом по раскрытию: в обычной работе удалённые
 * не нужны и не должны мешаться среди живых, а список их короткий.
 */
function DeletedFeeds() {
  const [gone, setGone] = useState<Feed[] | null>(null);
  const [failed, setFailed] = useState(false);
  const mounted = useMounted();

  return (
    <details onToggle={(e) => {
      if ((e.target as HTMLDetailsElement).open && gone === null) {
        void listDeletedFeeds()
          .then((found) => { if (mounted.current) setGone(found); })
          .catch(() => { if (mounted.current) setFailed(true); });
      }
    }}>
      <summary>Удалённые выгрузки</summary>
      <p className="note">
        Удалённая выгрузка не отдаёт прайс и не занимает ни названия, ни имени
        файла — но помнит, когда площадка последний раз забирала у неё прайс.
      </p>
      {failed && <p className="note note--error">Удалённые выгрузки не загрузились</p>}
      {gone !== null && gone.length === 0 && !failed && (
        <p className="muted">Удалённых выгрузок нет</p>
      )}
      {gone !== null && gone.length > 0 && (
        <ul className="cards">
          {gone.map((feed) => (
            <li className="card" key={feed.id}>
              <div className="order-head">
                <strong>{feed.title}</strong>
                <span>
                  {feed.marketplace === 'AVITO' ? 'Авито' : 'Дром'}
                  {feed.productLine === 'WHEEL' ? ' · шины и диски' : ''}
                </span>
              </div>
              <p className="muted">{deletedMark(feed)}</p>
              <p className="muted">{downloadMark(feed)}</p>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}

function FeedCard({
  feed,
  role,
  warehouses,
  kinds,
  brands,
  onChanged,
  onError,
}: {
  feed: Feed;
  /** Выключает и удаляет выгрузку только владелец: это остановка канала продаж. */
  role: string;
  warehouses: Warehouse[];
  kinds: PartKind[];
  brands: Brand[];
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  // Через String: с сервера цена приходит числом, и `?? ''` оставил бы
  // в состоянии число — а поле правится как строка.
  const [priceFrom, setPriceFrom] = useState(
    feed.priceFrom === null ? '' : String(feed.priceFrom));
  const [priceTo, setPriceTo] = useState(
    feed.priceTo === null ? '' : String(feed.priceTo));
  const [conditions, setConditions] = useState<string[]>(feed.conditions);
  const [warehouseIds, setWarehouseIds] = useState<number[]>(feed.warehouseIds);
  const [link, setLink] = useState<FeedLink | null>(null);
  const [secret, setSecret] = useState('');
  // Номер прайс-листа — не секрет, поле показывает текущий.
  const [packet, setPacket] = useState(feed.packetId ?? '');
  // Имя файла прайса: пусто — имени не задавали, и ссылка кончается токеном.
  const [fileName, setFileName] = useState(feed.feedFileName ?? '');

  // Настройки сборки прайса — через String по той же причине, что и цена:
  // с сервера они приходят числом, а поле правится строкой.
  const [pricePercent, setPricePercent] = useState(
    feed.settings?.pricePercent == null ? '' : String(feed.settings.pricePercent));
  const [priceRounding, setPriceRounding] = useState(
    feed.settings?.priceRounding == null ? '' : String(feed.settings.priceRounding));
  // Пусто — «не задано», то есть прежние десять. Ноль — «без ограничения»,
  // и превращать одно в другое здесь нельзя: на сервере это разные ответы.
  const [photoLimit, setPhotoLimit] = useState(
    feed.settings?.photoLimit == null ? '' : String(feed.settings.photoLimit));

  // Приписка про стоимость установки. Поле текста открывается заполненным
  // умолчанием: включённая приписка с пустым текстом ничего не допишет,
  // и владелец решит, что переключатель не работает.
  const [installationNote, setInstallationNote] = useState(
    feed.settings?.installationNote === true);
  const [installationTemplate, setInstallationTemplate] = useState(
    feed.settings?.installationTemplate ?? DEFAULT_INSTALLATION_TEMPLATE);

  // Товар, по которому ожидается поступление. Поле текста открывается пустым
  // намеренно: слова приписки выбирает владелец, а не мы, и подставленный
  // здесь текст уехал бы к покупателю от его имени. Пустое — законно: товар
  // уходит без приставки, как у Bazon (задача 0339).
  const [expectedGoods, setExpectedGoods] = useState(
    feed.settings?.expectedGoods === true);
  const [expectedGoodsNote, setExpectedGoodsNote] = useState(
    feed.settings?.expectedGoodsNote ?? '');

  // «Выгружать товары в резерве» (задача 0339): выключен, пока владелец
  // не включит, — как у Bazon. Приставка пуста по той же причине, что выше.
  const [reservedGoods, setReservedGoods] = useState(
    feed.settings?.reservedGoods === true);
  const [reservedGoodsNote, setReservedGoodsNote] = useState(
    feed.settings?.reservedGoodsNote ?? '');

  // Наименования, которым в объявление дописываются снимки машины-донора.
  // Пусто — никому, как было до появления настройки: у фары фотографии
  // донора лишние, а у двигателя решают продажу.
  const [donorPhotoKinds, setDonorPhotoKinds] = useState<number[]>(
    feed.settings?.donorPhotoKinds ?? []);

  const [busy, setBusy] = useState(false);
  const [matching, setMatching] = useState<number | null>(null);
  // Сколько из попавших в файл стоят 0 — красная строка рядом со счётчиком.
  const [zeroPrice, setZeroPrice] = useState(0);

  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  // Название правится тут же, где показано: ссылки это не касается вовсе.
  const [title, setTitle] = useState(feed.title);
  // Удаление спрашивает подтверждение и называет, что именно удаляется:
  // выгрузок у владельца пять, и различаются они только названием.
  const [confirming, setConfirming] = useState(false);

  // Свои условия владельца — те же колонки, какими он смотрит склад.
  const [columns, setColumns] = useState<Record<string, string>>(
    feed.filterColumns ?? {});
  const [words, setWords] = useState<Record<string, string>>(feed.filterWords ?? {});
  const [addFor, setAddFor] = useState('');
  const [typed, setTyped] = useState('');
  const [menuAt, setMenuAt] = useState<{ left: number; top: number } | null>(null);

  const wheels = feed.productLine === 'WHEEL';
  const known = wheels
    ? WHEEL_COLUMNS.map((c) => ({ key: c.key, title: c.title }))
    : COLUMNS.map((c) => ({ key: c.key, title: c.title }));
  const titleOf = (key: string) => known.find((c) => c.key === key)?.title ?? key;

  /*
   * Список отбираемых колонок спрашивается у сервера, а не повторяется здесь.
   *
   * Повторённый, он разошёлся бы с ним на первой же новой колонке — и экран
   * предлагал бы отбор, которого нет: сервер отвечает «по этой колонке отбор
   * не делается», а владелец видит те же тридцать пять тысяч позиций
   * и решает, что отбор сломан. Ровно этим уже болело меню колонки.
   */
  const [filterable, setFilterable] = useState<string[] | null>(null);
  useEffect(() => {
    let alive = true;
    void filterableColumns(feed.productLine)
      .then((list) => { if (alive) setFilterable(list); })
      .catch(() => { if (alive) setFilterable([]); });
    return () => { alive = false; };
  }, [feed.productLine]);

  const [kindIds, setKindIds] = useState<number[]>(feed.kindIds);
  const [kindsExcluded, setKindsExcluded] = useState(feed.kindsExcluded);
  const [brandIds, setBrandIds] = useState<number[]>(feed.brandIds);
  const [brandsExcluded, setBrandsExcluded] = useState(feed.brandsExcluded);

  const current = (): FeedFilter => ({
    priceFrom: priceFrom.trim() === '' ? null : priceFrom.trim(),
    priceTo: priceTo.trim() === '' ? null : priceTo.trim(),
    conditions,
    warehouseIds,
    kindIds,
    kindsExcluded,
    brandIds,
    brandsExcluded,
    columns,
    words,
  });

  async function save() {
    setBusy(true);
    try {
      await setFilter(feed.id, current());
      if (mounted.current) onChanged();
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Отбор не сохранён'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  /**
   * Настройки сборки прайса сохраняются отдельно от отбора.
   *
   * <p>Это разные решения: отбор меняет состав прайса, наценка — цену в нём.
   * Одной кнопкой они сохранялись бы двумя запросами, и владелец не понял бы,
   * что именно не сохранилось, если бы не прошёл один из них.
   *
   * <p><b>Уезжают все настройки разом, а не одно правленое поле.</b> Сервер
   * кладёт их слиянием по составу объекта, и запрос с одним полем записал бы
   * соседним `null`: сохранив число снимков, владелец потерял бы наценку —
   * и узнал бы об этом с чужого сайта. Поэтому у каждой кнопки своё
   * сообщение об отказе, но тело запроса одно и то же.
   */
  async function saveSettings(refused: string) {
    setBusy(true);
    try {
      await setSettings(feed.id, {
        pricePercent: decimalOrNull(pricePercent),
        priceRounding: decimalOrNull(priceRounding),
        photoLimit: wholeOrNull(photoLimit),
        installationNote,
        installationTemplate: installationTemplate.trim() === ''
          ? null : installationTemplate,
        expectedGoods,
        expectedGoodsNote: expectedGoodsNote.trim() === ''
          ? null : expectedGoodsNote,
        reservedGoods,
        reservedGoodsNote: reservedGoodsNote.trim() === ''
          ? null : reservedGoodsNote,
        donorPhotoKinds,
      });
      if (mounted.current) onChanged();
    } catch (cause) {
      if (mounted.current) onError(describe(cause, refused));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function count() {
    setBusy(true);
    try {
      // Переключатели ожидаемого товара и резерва берутся из формы, как и весь
      // отбор: счётчик, о них не знающий, обещал бы не то, что уедет площадке.
      const counted = await countMatching(
        current(), feed.productLine, expectedGoods, reservedGoods);
      if (mounted.current) {
        setMatching(counted.parts);
        setZeroPrice(counted.zeroPrice ?? 0);
      }
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Посчитать не удалось'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  /**
   * Переименование.
   *
   * <p>Ссылку не перечитываем и не показываем — она не менялась, и намекать
   * на обратное значило бы отправить владельца заново договариваться
   * с техспециалистом площадки.
   */
  async function rename() {
    setBusy(true);
    try {
      await renameFeed(feed.id, title.trim());
      if (mounted.current) onChanged();
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Название не сохранено'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function switchTo(status: 'ACTIVE' | 'PAUSED') {
    setBusy(true);
    try {
      await setFeedStatus(feed.id, status);
      if (mounted.current) onChanged();
    } catch (cause) {
      if (mounted.current) {
        onError(describe(cause,
          status === 'PAUSED' ? 'Выгрузка не выключена' : 'Выгрузка не включена'));
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await deleteFeed(feed.id);
      // Карточка исчезнет вместе с перечитанным списком, но подтверждение
      // закрываем сами: отказ оставил бы его открытым, и владелец нажал бы
      // «Удалить» второй раз, не поняв, что первое не прошло.
      if (mounted.current) {
        setConfirming(false);
        onChanged();
      }
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Выгрузка не удалена'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function rotate() {
    setBusy(true);
    try {
      const rotated = await rotateFeedUrl(feed.id);
      if (mounted.current) {
        setLink(rotated);
        onChanged();
      }
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Ссылка не сменилась'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  /**
   * Сохраняет имя файла и сразу перечитывает ссылку.
   *
   * <p>Ради ссылки имя и задают: показать «сохранено», оставив на экране
   * прежний адрес, значило бы заставить владельца гадать, попало ли имя
   * в ссылку, — а перенести в кабинет площадки он должен именно её.
   */
  async function saveFileName() {
    setBusy(true);
    try {
      await setFeedFileName(feed.id, fileName.trim());
      if (feed.hasFeed) {
        const fresh = await feedUrl(feed.id);
        if (mounted.current) setLink(fresh);
      }
      if (mounted.current) onChanged();
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Имя файла не сохранено'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  const menu = addFor !== '' && menuAt !== null ? (
    <ColumnMenu
      column={addFor}
      at={menuAt}
      chosen={columns[addFor]}
      filterable
      sortable={undefined}
      sort=""
      desc={false}
      values={wheels ? wheelValues : columnValues}
      empty={FILTER_EMPTY}
      present={FILTER_PRESENT}
      onSort={() => {}}
      onPick={(value) => {
        const chosen = { ...columns };
        if (value === null) {
          delete chosen[addFor];
        } else {
          chosen[addFor] = value;
        }
        setColumns(chosen);
        setMenuAt(null);
        setAddFor('');
      }}
      onClose={() => setMenuAt(null)}
    />
  ) : null;

  // Одно выражение на кнопку «Сохранить номер» и на подпись под ней.
  const packetObstacle = packetIdObstacle(packet, feed.packetId);
  const delivery = deliveryLine(feed);

  return (
    <li className="card">
      <div className="order-head">
        <strong>{feed.title}</strong>
        <span>
          {feed.marketplace === 'AVITO' ? 'Авито' : 'Дром'}
          {/* Вид товара — не украшение: две выгрузки на одну площадку иначе
              различаются только содержимым файла, а его открывают раз в жизни. */}
          {feed.productLine === 'WHEEL' ? ' · шины и диски' : ''}
        </span>
      </div>
      <p className="muted">{filterSummary(feed)}</p>
      {/* Первым делом спрашивают не про отбор, а про то, уехал ли прайс
          вообще: до этой строки ответ жил только в логах приложения. */}
      <p className="muted">{downloadMark(feed)}</p>

      {/* Выключенная выгрузка выглядела бы работающей: отбор на месте, ссылка
          на месте, отметка о заборе на месте — а прайс площадка не забирает
          вовсе. Поэтому состояние сказано словами и рядом сказано, что оно
          значит для площадки. */}
      {feed.status === 'PAUSED' && (
        <p className="note">
          Выгрузка выключена: площадка получает «прайса нет» и объявления
          остаются на месте, пока её не включат обратно.
        </p>
      )}

      {/* Итог последней отправки дельт — рядом с «Скачан …», потому что оба
          отвечают на «уехало ли», а факты разные: забрали прайс и приняли
          дельту. Слова вместо кода Дрома; красным — только отказ, у которого
          есть действие (задача 0268). */}
      {delivery && (
        <p className={delivery.error ? 'note note--error' : 'note'}>{delivery.text}</p>
      )}
      {feed.lastError && feed.marketplace === 'AVITO' && (
        <p className="note note--error">{feed.lastError}</p>
      )}
      {feed.lastError && feed.marketplace !== 'AVITO' && (
        <details>
          <summary>Ответ Дрома</summary>
          <p className="muted">{feed.lastError}</p>
        </details>
      )}

      <div className="filter-row">
        <label className="field">
          Цена от, ₽
          <input
            inputMode="decimal"
            value={priceFrom}
            placeholder="без границы"
            onChange={(e) => setPriceFrom(e.target.value)}
          />
        </label>
        <label className="field">
          Цена до, ₽
          <input
            inputMode="decimal"
            value={priceTo}
            placeholder="без границы"
            onChange={(e) => setPriceTo(e.target.value)}
          />
        </label>
      </div>

      <fieldset className="choices">
        <legend>Состояние — пусто значит любое</legend>
        {CONDITIONS.map((condition) => (
          <label key={condition.code}>
            <input
              type="checkbox"
              checked={conditions.includes(condition.code)}
              onChange={() => setConditions(toggle(conditions, condition.code))}
            />
            {condition.name}
          </label>
        ))}
      </fieldset>

      {warehouses.length > 1 && (
        <fieldset className="choices">
          <legend>Склады — пусто значит все</legend>
          {warehouses.map((warehouse) => (
            <label key={warehouse.id}>
              <input
                type="checkbox"
                checked={warehouseIds.includes(warehouse.id)}
                onChange={() => setWarehouseIds(toggle(warehouseIds, warehouse.id))}
              />
              {warehouse.name}
            </label>
          ))}
        </fieldset>
      )}

      {/* Вид детали и марка машины есть только у запчасти. У колеса part_kind
          не заполнен вовсе, а марка — это Dunlop, а не Toyota; прайс колёс
          их и не отбирает. Показанный тут отбор был бы обещанием, которого
          нет: владелец сузил бы выгрузку, а уехал бы весь склад колёс. */}
      {feed.productLine === 'PART' && (
        <>
          <Picker
            title="Наименования"
            options={kinds.map((k) => ({ id: k.id, name: k.name }))}
            chosen={kindIds}
            excluded={kindsExcluded}
            onChosen={setKindIds}
            onExcluded={setKindsExcluded}
          />

          <Picker
            title="Марки"
            options={brands.map((b) => ({ id: b.id, name: b.nameRu ?? b.name }))}
            chosen={brandIds}
            excluded={brandsExcluded}
            onChosen={setBrandIds}
            onExcluded={setBrandsExcluded}
          />
        </>
      )}

      {/* Свои условия владельца.
          Шесть зашитых условий покрывают не всё, а каждое седьмое означало бы
          релиз: миграция, генератор, счётчик, экран. Витрина склада к этому
          времени отбирает по двадцати девяти колонкам, и выгрузка берёт тот же
          механизм — те же колонки, те же значения, тот же разбор «пусто».
          Второй список колонок рядом с витринным разошёлся бы с ним
          на первой правке. */}
      <fieldset className="choices">
        <legend>Свои условия — пусто значит без ограничения</legend>

        {/* Тем же значком, что и на витрине склада: условия там снимаются
            нажатием на сам значок, и второй вид того же элемента заставлял бы
            владельца заново догадываться, как его убрать. */}
        {Object.entries(columns).map(([key, value]) => (
          <button
            key={`c-${key}`}
            type="button"
            className="crumb"
            onClick={() => {
              const left = { ...columns };
              delete left[key];
              setColumns(left);
            }}
          >
            {titleOf(key)}: {value} ✕
          </button>
        ))}
        {Object.entries(words).map(([key, value]) => (
          <button
            key={`w-${key}`}
            type="button"
            className="crumb"
            onClick={() => {
              const left = { ...words };
              delete left[key];
              setWords(left);
            }}
          >
            {titleOf(key)}: «{value}» ✕
          </button>
        ))}

        <div className="filter-row">
          <label className="field">
            Колонка
            <select value={addFor} onChange={(e) => {
              setAddFor(e.target.value);
              setTyped('');
              setMenuAt(null);
            }}>
              <option value="">— выберите колонку —</option>
              {known
                .filter((c) => (filterable ?? []).includes(c.key))
                .map((c) => (
                  <option key={c.key} value={c.key}>{c.title}</option>
                ))}
            </select>
          </label>

          {/* Два способа, как на витрине: вбитое руками ищется вхождением
              («Nok» находит Nokian), выбранное из списка — точным равенством.
              Это разные вопросы, и различать их магией в значении нельзя. */}
          <label className="field">
            Содержит
            <input
              value={typed}
              placeholder="часть значения"
              disabled={addFor === ''}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>

          <button
            type="button"
            className="button--ghost"
            disabled={addFor === '' || typed.trim() === ''}
            onClick={() => {
              setWords({ ...words, [addFor]: typed.trim() });
              setAddFor('');
              setTyped('');
            }}
          >
            Добавить
          </button>

          <button
            type="button"
            className="button--ghost"
            disabled={addFor === ''}
            onClick={(e) => {
              // Без прокрутки: список значений рисуется `position: fixed`,
              // и прибавленный scrollY уводит его за нижний край экрана —
              // кнопка нажата, а ответа на ней нет.
              const box = (e.target as HTMLElement).getBoundingClientRect();
              setMenuAt({ left: box.left, top: box.bottom });
            }}
          >
            Выбрать значение
          </button>
        </div>

        {filterable !== null && filterable.length === 0 && (
          <p className="muted">Список колонок не прочитан — обновите страницу</p>
        )}
      </fieldset>

      {/* Цена в прайсе — настройка сборки файла, а не отбора.
          Площадка берёт комиссию, и продавцы закладывают её в цену
          объявления: у живого клиента на прайсе Авито стоит −20 %. Без этого
          заложить комиссию можно было только испортив цену для прилавка —
          то есть для того же товара в зале и по телефону. */}
      <fieldset className="choices">
        <legend>Цена в прайсе — пусто значит как на складе</legend>
        <p className="note">
          Наценка или скидка действует только на этот прайс-лист. На складе,
          на витрине и у продавца цена остаётся прежней.
        </p>

        <div className="filter-row">
          <label className="field">
            Наценка или скидка, %
            <input
              inputMode="decimal"
              value={pricePercent}
              placeholder="например, -20"
              onChange={(e) => setPricePercent(e.target.value)}
            />
          </label>

          {/* Шаги те же, что у системы, с которой переходят клиенты:
              «5 940» вместо «5 938,20» — цена, которую продавец назовёт
              по телефону не запинаясь. Округление идёт вверх: вниз оно
              однажды отдаст деталь дешевле, чем владелец задал. */}
          <label className="field">
            Округлять до
            <select
              value={priceRounding}
              onChange={(e) => setPriceRounding(e.target.value)}
            >
              <option value="">не округлять</option>
              {ROUNDING_STEPS.map((step) => (
                <option key={step} value={step}>{step}</option>
              ))}
            </select>
          </label>

          <button type="button" disabled={busy}
                  onClick={() => void saveSettings('Цена прайса не сохранена')}>
            Сохранить цену прайса
          </button>
        </div>
      </fieldset>

      {/* Сколько снимков уходит в объявление — тоже настройка сборки файла,
          а не отбора. Предел в десять был зашит в сборку прайса, то есть
          правка его означала релиз, а площадки считают снимки по-разному:
          где-то десять лишние, где-то мало. */}
      <fieldset className="choices">
        <legend>Снимки в объявлении — пусто значит десять</legend>
        <p className="note">
          Ноль — без ограничения: уедут все снимки позиции. Уходят они
          в том же порядке, в каком показываются, — главный первым.
        </p>

        <div className="filter-row">
          <label className="field">
            Число фотографий в объявлении
            <input
              inputMode="numeric"
              value={photoLimit}
              placeholder="например, 3"
              onChange={(e) => setPhotoLimit(e.target.value)}
            />
          </label>

          <button type="button" disabled={busy}
                  onClick={() => void saveSettings('Число снимков не сохранено')}>
            Сохранить число снимков
          </button>
        </div>
      </fieldset>

      {/* Стоимость установки в объявлении. Услуга у клиента заведена и стоит
          денег: поле «Цена установки» есть в карточке и в отборе выгрузки,
          а до объявления не доезжало ни одной строкой — то есть услуга была
          невидима ровно там, где её покупают. Позиция без цены установки
          строки не получает: пусто у нас значит «услуги нет», а не
          «бесплатно». */}
      <fieldset className="choices">
        <legend>Стоимость установки — приписка к описанию</legend>
        <p className="note">
          Дописывается в конец описания объявления. Товар, у которого цена
          установки не заполнена, уезжает без этой строки. Место цены
          в тексте — {PRICE_PLACEHOLDER}.
        </p>

        {/* Подпись длинная, а флажки отбора выше стоят с `nowrap` — рассчитан
            он на «новые» и «б/у». Без переноса эта фраза не сжимается
            и уводит вбок всю страницу на телефоне. */}
        <label className="choice--wrap">
          <input
            type="checkbox"
            checked={installationNote}
            onChange={(e) => setInstallationNote(e.target.checked)}
          />
          Дописывать стоимость установки к описанию
        </label>

        {/* Поле стоит прямым элементом набора, а не внутри `filter-row`:
            ряд внутри `.choices` шириной с содержимое, и поле фразы
            в нём обрезается на середине — владелец видит начало того,
            что пишет, и не видит, чем это кончается. */}
        <label className="field field--sentence">
          Текст приписки
          <input
            value={installationTemplate}
            placeholder={DEFAULT_INSTALLATION_TEMPLATE}
            onChange={(e) => setInstallationTemplate(e.target.value)}
          />
        </label>

        <div className="filter-row">
          <button type="button" disabled={busy}
                  onClick={() => void saveSettings('Приписка не сохранена')}>
            Сохранить приписку
          </button>
        </div>
      </fieldset>

      {/* Позиция, целиком отложенная под клиента (задача 0339). Подписи —
          дословно Bazon: клиент переходит оттуда и ищет эти слова. Поле
          приставки погашено, пока переключатель выключен, — как у Bazon.

          Колёсной выгрузке не показываем: генератор колёс резерва не знает,
          отложенное колесо в файл не идёт никогда. */}
      {!wheels && (
        <fieldset className="choices">
          <legend>Товар в резерве</legend>
          <label className="choice--wrap">
            <input
              type="checkbox"
              checked={reservedGoods}
              onChange={(e) => setReservedGoods(e.target.checked)}
            />
            Выгружать товары в резерве
          </label>
          <label className="field field--sentence">
            Приставка о резерве
            <input
              value={reservedGoodsNote}
              disabled={!reservedGoods}
              onChange={(e) => setReservedGoodsNote(e.target.value)}
            />
          </label>
          <p className="note">
            Если товар находится в резерве, то информация об этом добавится
            в начало объявления
          </p>
          <div className="filter-row">
            <button type="button" disabled={busy}
                    onClick={() => void saveSettings('Товар в резерве не сохранён')}>
              Сохранить товар в резерве
            </button>
          </div>
        </fieldset>
      )}

      {/* Товар, по которому ожидается поступление: он привязан к поставке
          в пути и на складе его ещё нет. Подпись под переключателем — Bazon
          дословно (задача 0339): у него это товар в перемещении между
          складами, у нас пока — из поставки в пути; перемещение войдёт
          сюда же с задачей 0370. Приставка необязательна, как у Bazon.

          Колёсной выгрузке этого не показываем: в прайсе шин и дисков
          элемента описания нет вовсе, приписку дописывать некуда. */}
      {!wheels && (
        <fieldset className="choices">
          <legend>Товар в пути</legend>

          {/* Подпись длинная, а флажки отбора выше стоят с `nowrap` —
              рассчитан он на «новые» и «б/у». Без переноса эта фраза
              не сжимается и уводит вбок всю страницу на телефоне. */}
          <label className="choice--wrap">
            <input
              type="checkbox"
              checked={expectedGoods}
              onChange={(e) => setExpectedGoods(e.target.checked)}
            />
            Выгружать товары, по которым ожидается поступление
          </label>

          {/* Поле стоит прямым элементом набора, а не внутри `filter-row`:
              ряд внутри `.choices` шириной с содержимое, и поле фразы в нём
              обрезается на середине. */}
          {/* Подпись не просто «Текст приписки»: такая уже стоит у стоимости
              установки, и две одинаковые подписи на одном экране владелец
              читает как одно и то же поле. */}
          <label className="field field--sentence">
            Текст приписки о поступлении
            <input
              value={expectedGoodsNote}
              disabled={!expectedGoods}
              placeholder="например, ожидается поступление"
              onChange={(e) => setExpectedGoodsNote(e.target.value)}
            />
          </label>
          <p className="note">
            Если товар находится в перемещении, то информация об этом
            добавится в начало объявления
          </p>

          <div className="filter-row">
            <button type="button" disabled={busy}
                    onClick={() => void saveSettings('Товар в пути не сохранён')}>
              Сохранить товар в пути
            </button>
          </div>
        </fieldset>
      )}

      {/* Снимки машины-донора в объявлении. Для двигателя и коробки состояние
          машины — половина объявления: покупатель смотрит, откуда снято,
          какой пробег, цел ли кузов. У фары те же фотографии лишние, поэтому
          владелец перечисляет наименования, а не включает «всем».

          Колёсной выгрузке этого не показываем: донора у колеса нет вовсе
          (прайс шин его и не отбирает), и показанная настройка была бы
          обещанием, которого нет. */}
      {!wheels && (
        <fieldset className="choices">
          <legend>Снимки машины-донора — каким наименованиям дописывать</legend>
          <p className="note">
            Уходят вслед за своими снимками позиции и в пределах того же числа
            фотографий. Пусто — не дописывать никому. У контрактной детали,
            приехавшей контейнером, донора нет — её объявление не меняется.
          </p>

          {/* Без выбора направления: «все, кроме этих» здесь не бывает.
              Отбор решает, что уедет в прайс, и исключающая сторона ему
              нужна («двигатели сюда не выгружать»); тут решается, кому
              дописать снимки машины, и «дописать всем, кроме фары» — это
              фотографии донора у девятисот наименований, то есть ровно
              то, от чего настройка и избавляет. Показанный переключатель
              без действия был бы мёртвым управлением на экране. */}
          <Picker
            title="Наименования со снимками машины"
            options={kinds.map((k) => ({ id: k.id, name: k.name }))}
            chosen={donorPhotoKinds}
            onChosen={setDonorPhotoKinds}
          />

          <div className="filter-row">
            <button type="button" disabled={busy}
                    onClick={() => void saveSettings('Снимки донора не сохранены')}>
              Сохранить снимки донора
            </button>
          </div>
        </fieldset>
      )}

      <div className="filter-row">
        <button type="button" className="button--ghost" disabled={busy}
                onClick={() => void count()}>
          Посчитать
        </button>
        <button type="button" disabled={busy} onClick={() => void save()}>
          Сохранить отбор
        </button>
      </div>

      {matching !== null && (
        <p className={matching === 0 ? 'note note--error' : 'note'}>
          {matching === 0
            ? 'С таким отбором в прайс не попадёт ничего. Площадка примет пустой '
              + 'прайс молча, и объявления пропадут вместе с просмотрами'
            : `В прайс попадёт позиций: ${matching}`}
        </p>
      )}
      {/* Цена 0 уходит на площадку, как у Bazon (задача 0339), — и обычно
          это поле, которое не заполнили. Слова — из решения владельца. */}
      {matching !== null && matching > 0 && zeroPrice > 0 && (
        <p className="note note--error">
          {`с нулевой ценой: ${zeroPrice.toLocaleString('ru-RU')}`}
        </p>
      )}

      {/* Ссылка спрашивается при раскрытии, а не показывается только сразу
          после выдачи. Иначе узнать её нельзя вовсе: владелец завёл прайс,
          отдал адрес техспециалисту площадки, а через неделю адрес спросили
          снова — и посмотреть его негде. Единственной кнопкой была «Сменить
          ссылку», которая, как честно написано рядом, выгрузку останавливает:
          чтобы узнать ссылку, приходилось её сломать. */}
      <details onToggle={(e) => {
        if ((e.target as HTMLDetailsElement).open && link === null && feed.hasFeed) {
          void feedUrl(feed.id)
            .then((fresh) => { if (mounted.current) setLink(fresh); })
            .catch(() => {});
        }
      }}>
        <summary>Ссылка для площадки</summary>
        <p className="note">
          Смена ссылки останавливает выгрузку: новую в кабинет площадки
          прописывает её техспециалист руками, и до этого прайс забирать
          будет неоткуда.
        </p>
        {link !== null && link.path !== null && (
          <>
            {/* Полный адрес, а не путь: его копируют и отдают человеку
                на той стороне, и по «/feeds/drom/…» тот не сходит никуда. */}
            <p className="muted">{link.url ?? link.path}</p>
            {/* Скачать — потому что заливка файлом руками это не запасной
                путь, а единственный быстрый: забор по ссылке идёт раз
                в трое суток, и до него новая деталь на площадке
                не появится. Ссылкой, а не кнопкой с запросом: файл
                на двадцать мегабайт качает браузер, показывая ход. */}
            <p>
              <a href={link.path} download>Скачать файл прайса</a>
            </p>
          </>
        )}
        {/* Имя файла — читаемый хвост адреса. Техспециалист площадки
            переносит ссылку руками, и сорок случайных символов токена
            в конце он переписывает с ошибками: заметить это можно только
            по тому, что объявления не появились. Секрет при этом остаётся
            на месте — сохранение имени выгрузку не останавливает. */}
        <label className="field">
          Имя файла прайса
          <input
            value={fileName}
            placeholder="drom-parts.xml"
            onChange={(e) => setFileName(e.target.value)}
          />
        </label>
        <p className="note">
          Латинские буквы, цифры и дефис — это часть адреса. Пусто — ссылка
          кончается секретом, как раньше.
        </p>
        <button type="button" className="button--ghost" disabled={busy}
                onClick={() => void saveFileName()}>
          {feed.feedFileName ? 'Сохранить имя файла' : 'Задать имя файла'}
        </button>
        <button type="button" className="button--ghost" disabled={busy}
                onClick={() => void rotate()}>
          {feed.hasFeed ? 'Сменить ссылку' : 'Выдать ссылку'}
        </button>
      </details>
      {menu}

      {/*
        * Ключ синхронизации вводится здесь, и до этого его нельзя было
        * ввести нигде.
        *
        * Экран рядом честно писал «ключ к ним Дром выдаёт по заявке»,
        * а поля не было: `PUT /credentials` не звала ни одна строка
        * фронтенда. Без ключа дельты не уходят вовсе — принятая деталь,
        * подорожавшая или проданная, ждёт полного забора прайса, то есть
        * до трёх суток на бесплатном размещении. Заметить это нельзя:
        * очередь разгребается, `publication_log` пуст, и всё выглядит
        * работающим.
        *
        * Поле всегда пустое: прочитать ключ нельзя ни одним эндпоинтом,
        * и это часть защиты, а не недоделка. Состояние показывает
        * `hasCredentials`.
        */}
      <details>
        <summary>Ключ синхронизации {feed.hasCredentials ? '· задан' : '· не задан'}</summary>
        <p className="note">
          {feed.hasCredentials
            ? 'Ключ хранится зашифрованным и наружу не отдаётся: показать его нельзя, можно только заменить.'
            : 'Без ключа дельты по API не уходят: цена и остаток обновятся у площадки только с полным прайсом, а его забирают раз в трое суток.'}
        </p>
        <p className="note">
          Ключ выдаёт поддержка Дрома по обращению — он один на кабинет,
          выглядит как UUID и в кабинете площадки нигде не показывается.
          Обновление по API включают отдельно каждому прайс-листу.
        </p>
        <label>
          Ключ
          <input
            type="password"
            value={secret}
            autoComplete="off"
            placeholder="00000000-0000-0000-0000-000000000000"
            onChange={(e) => setSecret(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={busy || secret.trim() === ''}
          onClick={() => void saveSecret()}
        >
          {feed.hasCredentials ? 'Заменить ключ' : 'Сохранить ключ'}
        </button>
      </details>

      {/*
        * Номер прайс-листа в кабинете площадки.
        *
        * Появляется у владельца после того, как специалист Дрома разместил
        * прайс по нашей ссылке, то есть когда выгрузка давно заведена; а
        * принимал его только `POST` при заведении. Без номера дельты молча
        * не уходили, сколько бы раз ни сохранили ключ. Только владелец, как
        * и сервер. Пустое поле снимает номер.
        */}
      {role === 'OWNER' && (
        <details>
          <summary>Номер прайс-листа {feed.packetId ? '· задан' : '· не задан'}</summary>
          <label>
            Номер прайс-листа в кабинете
            <input
              value={packet}
              placeholder="12345"
              onChange={(e) => setPacket(e.target.value)}
            />
          </label>
          <p className="note">{PACKET_ID_HINT}</p>
          {packet.trim() === '' && feed.packetId && (
            <p className="note">Пустое поле снимет номер: дельты для этой выгрузки перестанут уходить.</p>
          )}
          {packetObstacle !== null && <p className="note">{packetObstacle}</p>}
          <button
            type="button"
            disabled={busy || packetObstacle !== null}
            onClick={() => void savePacket()}
          >
            Сохранить номер
          </button>
        </details>
      )}

      {/*
        * Переименовать, выключить и удалить.
        *
        * Выключить прайс-лист было нечем вовсе: его закрывают на сезон или
        * отказываются от него, а ссылка оставалась живой — площадка
        * продолжала забирать товар, которого клиент там видеть не хочет.
        * Единственным обходным путём была смена ссылки, то есть поломка
        * адреса, уже прописанного в кабинете площадки, без единого слова
        * о том, почему он перестал работать.
        *
        * Только владельцу: это остановка канала продаж, а отбор и цену
        * прайса правит и управляющий.
        */}
      {role === 'OWNER' && (
        <fieldset className="choices">
          <legend>Название и состояние</legend>

          <div className="filter-row">
            <label className="field">
              Название
              <input
                value={title}
                placeholder="например, Дром: основной"
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="button--ghost"
              disabled={busy || title.trim() === '' || title.trim() === feed.title}
              onClick={() => void rename()}
            >
              Переименовать
            </button>
          </div>
          <p className="note">
            Ссылка на прайс от переименования не меняется: её прописывает
            в кабинете площадки техспециалист руками, и правка названия
            не должна останавливать выгрузку.
          </p>

          <div className="filter-row">
            <span className="muted">Состояние: {statusLabel(feed)}</span>
            <button
              type="button"
              className="button--ghost"
              disabled={busy}
              onClick={() => void switchTo(feed.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED')}
            >
              {feed.status === 'PAUSED' ? 'Включить выгрузку' : 'Выключить выгрузку'}
            </button>
            {!confirming && (
              <button
                type="button"
                className="button--ghost"
                disabled={busy}
                onClick={() => setConfirming(true)}
              >
                Удалить выгрузку
              </button>
            )}
          </div>
          <p className="note">
            Выключенная выгрузка не отдаёт прайс вовсе — площадка понимает,
            что его нет, и объявления остаются. Пустой прайс она поняла бы
            как «снять всё», вместе с накопленными просмотрами.
          </p>

          {/* Подтверждение называет, что именно удаляется: прайс-листов
              у владельца пять, и различаются они только названием. */}
          {confirming && (
            <div className="note note--error">
              <p>
                Удалить выгрузку «{feed.title}»
                {feed.feedFileName ? ` (${feed.feedFileName})` : ''}? Ссылка
                перестанет работать, и площадка больше не заберёт по ней прайс.
                Отметки о заборе останутся в разделе «Удалённые выгрузки»,
                а название и имя файла освободятся.
              </p>
              <button type="button" disabled={busy} onClick={() => void remove()}>
                Да, удалить «{feed.title}»
              </button>
              <button
                type="button"
                className="button--ghost"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Отмена
              </button>
            </div>
          )}
        </fieldset>
      )}
    </li>
  );

  async function savePacket(): Promise<void> {
    setBusy(true);
    try {
      const saved = await savePacketId(feed.id, packet);
      if (mounted.current) {
        setPacket(saved.packetId ?? '');
        onChanged();
      }
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Номер не сохранён'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function saveSecret(): Promise<void> {
    setBusy(true);
    try {
      await setCredentials(feed.id, secret.trim());
      // Поле чистим сразу: ключ прочитать нельзя, и оставленный в поле
      // он выглядел бы как «мы его вам показываем».
      if (mounted.current) {
        setSecret('');
        onChanged();
      }
    } catch (cause) {
      if (mounted.current) onError(describe(cause, 'Ключ не сохранён'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
}

/**
 * Выбор из справочника: что попадает в выгрузку или что из неё исключено.
 *
 * <p><b>Направление названо словом, а не галочкой «инвертировать».</b>
 * «Только эти» и «кроме этих» — противоположные решения, и перепутать их
 * значит выгрузить ровно то, что выгружать не хотели.
 *
 * <p>Список показывается не весь: сто семьдесят восемь строк подряд никто
 * не читает. Поиск идёт по уже загруженному справочнику — он статичный
 * и маленький, запрос на каждую букву тут был бы данью привычке.
 *
 * <p>Выбранное показывается отдельно и всегда: иначе, стерев поиск, владелец
 * перестаёт видеть, что вообще выбрано, — а это и есть причина, по которой
 * прайс короче склада.
 */
function Picker({
  title,
  options,
  chosen,
  excluded,
  onChosen,
  onExcluded,
}: {
  title: string;
  options: Array<{ id: number; name: string }>;
  chosen: number[];
  /**
   * Направление отбора. Необязательное: у выбора, где исключающей стороны
   * не бывает (наименования со снимками машины-донора), переключателя быть
   * не должно вовсе — показанный, но ничего не меняющий, он читается как
   * сломанный.
   */
  excluded?: boolean;
  onChosen: (ids: number[]) => void;
  onExcluded?: (value: boolean) => void;
}) {
  const [query, setQuery] = useState('');

  const found = query.trim() === ''
    ? []
    : options
        .filter((o) => o.name.toLowerCase().includes(query.trim().toLowerCase()))
        .slice(0, 12);

  const chosenNames = chosen.map((id) => ({
    id,
    // Название могло исчезнуть из справочника между релизами. Пустая строка
    // читалась бы как «выбранное пропало», а номер строки базы владельцу
    // не говорит ничего — он всё равно может только снять этот пункт.
    name: options.find((o) => o.id === id)?.name ?? 'название недоступно',
  }));

  return (
    <fieldset className="picker">
      <legend>{title}</legend>

      {chosen.length > 0 && onExcluded !== undefined && (
        <label className="field">
          Направление
          <select
            value={excluded === true ? 'exclude' : 'include'}
            onChange={(e) => onExcluded(e.target.value === 'exclude')}
          >
            <option value="include">только эти</option>
            <option value="exclude">все, кроме этих</option>
          </select>
        </label>
      )}

      <label className="field">
        Найти
        <input
          value={query}
          placeholder="начните вводить"
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>

      {found.length > 0 && (
        <ul className="picker__found">
          {found.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className="button--ghost"
                disabled={chosen.includes(option.id)}
                onClick={() => onChosen([...chosen, option.id])}
              >
                {option.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      {chosen.length === 0 ? (
        <p className="muted">не ограничено</p>
      ) : (
        <ul className="picker__chosen">
          {chosenNames.map((option) => (
            <li key={option.id}>
              {option.name}
              <button
                type="button"
                className="button--ghost"
                onClick={() => onChosen(chosen.filter((id) => id !== option.id))}
              >
                убрать
              </button>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

/**
 * Заведение кабинета площадки.
 *
 * <p>Только Дром: генератор фида Авито написан, но отдавать его наружу
 * нечем, и предлагать в форме площадку, выгрузка на которую не работает, —
 * это обещание, которого нет.
 *
 * <p>Номер прайс-листа необязателен: постоянная ссылка работает без него,
 * он нужен дельтам по API — а ключ к ним Дром выдаёт по заявке.
 */
function NewFeed({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [packetId, setPacketId] = useState('');
  const [productLine, setProductLine] = useState<'PART' | 'WHEEL'>('PART');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useMounted();
  // Номер в форме заведения проверяется тем же правилом, что в карточке:
  // иначе «12 345» здесь проходил бы, а там отвергался (задача 0267).
  const packetUnreadable = normalizePacketId(packetId) === undefined;

  return (
    <div className="card">
      <h3>Завести выгрузку</h3>
      <p className="note">
        Выгрузок на одну площадку бывает несколько: у каждой свой отбор,
        свой прайс-лист в кабинете и своя цена размещения.
      </p>

      <label>
        Название
        <input
          value={title}
          placeholder="например, Дром: основной"
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>

      {/* Площадка требует держать шины отдельным прайс-листом, и требование
          по делу: у шины свои поля — маркировка, сезон, шиповка, износ, —
          а объявление «Шина 195/65 R15» среди запчастей уезжает в чужую
          категорию, откуда его снимают. */}
      <label>
        Что выгружаем
        <select
          value={productLine}
          onChange={(e) => setProductLine(e.target.value === 'WHEEL' ? 'WHEEL' : 'PART')}
        >
          <option value="PART">Запчасти</option>
          <option value="WHEEL">Шины и диски</option>
        </select>
      </label>

      <label>
        Номер прайс-листа в кабинете
        <input
          value={packetId}
          placeholder="необязательно"
          onChange={(e) => setPacketId(e.target.value)}
        />
      </label>
      <p className="note">{PACKET_ID_HINT}</p>
      {packetUnreadable && (
        <p className="note note--error">
          Номер не распознан: впишите номер из адреса прайс-листа или вставьте адрес целиком
          (не больше 32 знаков, без пробелов), либо оставьте поле пустым
        </p>
      )}

      {error !== '' && <p className="note note--error">{error}</p>}

      <button
        type="button"
        disabled={busy || title.trim() === '' || packetUnreadable}
        onClick={() => void save()}
      >
        {busy ? 'Заводим…' : 'Завести кабинет Дрома'}
      </button>
    </div>
  );

  async function save(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await createFeed(title.trim(), packetId, productLine);
      if (mounted.current) {
        setTitle('');
        setPacketId('');
        onCreated();
      }
    } catch (cause) {
      if (mounted.current) setError(describe(cause, 'Кабинет не заведён'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
}

function describe(cause: unknown, fallback: string): string {
  return cause instanceof ApiError && cause.message ? cause.message : fallback;
}
